// File: frontend/src/pages/DocumentGeneratorPage.js
// Version: 2.0 (quick-260915-d2d — зашитые цвета убраны, экран на общем слое)
// Date: 2026-09-15
// Description: Document generator — template picker, case selector, generate, edit, save, and PDF export.

import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import documentGeneratorService from '../services/documentGeneratorService';
import apiService from '../services/apiService';
// 🔴 ОБЩИЙ СЛОЙ ОФОРМЛЕНИЯ: `Sostoyanie` — единственный держатель того, как
//    экран говорит «грузим» и «пусто».
import { Sostoyanie } from '../components/FitenkoUzor';
import './DocumentGeneratorPage.css';

const DocumentGeneratorPage = () => {
  const [templates, setTemplates] = useState([]);
  const [cases, setCases] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [generatedDoc, setGeneratedDoc] = useState(null);
  const [editedBody, setEditedBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  // Load templates and cases on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [templatesData, casesData] = await Promise.all([
          documentGeneratorService.getTemplates(),
          apiService.getCases(),
        ]);
        setTemplates(templatesData || []);
        setCases(casesData || []);
      } catch (err) {
        console.error('[DocumentGenerator] initial load error:', err);
        toast.error('Failed to load templates or cases.');
      } finally {
        setInitialLoad(false);
      }
    };
    loadData();
  }, []);

  const handleGenerate = async () => {
    if (!selectedTemplateId || !selectedCaseId) {
      toast.error('Please select both a template and a case.');
      return;
    }
    setLoading(true);
    setGeneratedDoc(null);
    setEditedBody('');
    try {
      const doc = await documentGeneratorService.generateDocument(
        Number(selectedTemplateId),
        Number(selectedCaseId)
      );
      setGeneratedDoc(doc);
      setEditedBody(doc.body || '');
      toast.success('Document generated successfully.');
    } catch (err) {
      console.error('[DocumentGenerator] generateDocument error:', err);
      toast.error('Failed to generate document. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!generatedDoc) return;
    setSaving(true);
    try {
      const updated = await documentGeneratorService.updateDocument(generatedDoc.id, editedBody, false);
      setGeneratedDoc(updated);
      toast.success('Document saved successfully.');
    } catch (err) {
      console.error('[DocumentGenerator] updateDocument error:', err);
      toast.error('Failed to save document.');
    } finally {
      setSaving(false);
    }
  };

  const handleFinalizeAndDownload = async () => {
    if (!generatedDoc) return;
    setSaving(true);
    try {
      await documentGeneratorService.updateDocument(generatedDoc.id, editedBody, true);
      toast.success('Document finalized. Opening PDF...');
      window.open(documentGeneratorService.getExportUrl(generatedDoc.id), '_blank');
    } catch (err) {
      console.error('[DocumentGenerator] finalize/export error:', err);
      toast.error('Failed to finalize or export document.');
    } finally {
      setSaving(false);
    }
  };

  if (initialLoad) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-border border-t-primary" />
        <span className="ml-3.5"><Sostoyanie tekst="Loading document templates…" /></span>
      </div>
    );
  }

  return (
    <div className="doc-generator-container">
      <div className="doc-generator-header">
        <h1 className="page-title">Document Generator</h1>
        <p className="page-subtitle">Select a template and case to generate a pre-filled legal document.</p>
      </div>

      <div className="doc-generator-layout">
        {/* Left Panel — Controls */}
        <div className="doc-generator-controls">
          <div className="controls-card">
            <h2 className="controls-heading">Generate Document</h2>

            <div className="form-group">
              <label htmlFor="template-select" className="form-label">Template</label>
              <select
                id="template-select"
                className="form-select"
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
              >
                <option value="">— Select a template —</option>
                {templates.map((tmpl) => (
                  <option key={tmpl.id} value={tmpl.id}>
                    {tmpl.name} ({tmpl.doc_type})
                  </option>
                ))}
              </select>
              {templates.length === 0 && (
                <p className="form-hint">No templates available. Contact your administrator.</p>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="case-select" className="form-label">Case</label>
              <select
                id="case-select"
                className="form-select"
                value={selectedCaseId}
                onChange={(e) => setSelectedCaseId(e.target.value)}
              >
                <option value="">— Select a case —</option>
                {cases.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.case_number} — {c.title || c.case_type}
                  </option>
                ))}
              </select>
            </div>

            <button
              className="btn-generate"
              onClick={handleGenerate}
              disabled={loading || !selectedTemplateId || !selectedCaseId}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  Generating...
                </span>
              ) : (
                'Generate Document'
              )}
            </button>

            {generatedDoc && (
              <div className="doc-meta">
                <p className="text-xs text-muted-foreground">
                  Document ID: <span className="font-mono font-bold dg-ink-accent">#{generatedDoc.id}</span>
                </p>
                <p className="text-xs text-muted-foreground flex items-center gap-2">
                  Status: 
                  <span className={`px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider ${
                    generatedDoc.is_finalized 
                      ? 'dg-pill dg-pill-final'
                      : 'dg-pill dg-pill-draft'
                  }`}>
                    {generatedDoc.is_finalized ? 'Finalized' : 'Draft'}
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Right Panel — Editor */}
        <div className="doc-generator-editor">
          {generatedDoc ? (
            <div className="editor-card">
              <div className="editor-toolbar">
                <h2 className="controls-heading mb-0">Generated Document</h2>
                <div className="editor-actions">
                  <button
                    className="btn-save"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Draft'}
                  </button>
                  <button
                    className="btn-finalize"
                    onClick={handleFinalizeAndDownload}
                    disabled={saving}
                  >
                    Finalize & Download PDF
                  </button>
                </div>
              </div>
              <textarea
                className="doc-textarea"
                rows={20}
                value={editedBody}
                onChange={(e) => setEditedBody(e.target.value)}
                placeholder="Document content will appear here..."
              />
            </div>
          ) : (
            <div className="editor-empty">
              <div className="w-14 h-14 mb-3 rounded-full bg-accent flex items-center justify-center border border-primary/30">
                <svg className="w-7 h-7 dg-ink-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p className="text-base font-medium text-foreground dg-display">
                {loading ? 'Generating legal document...' : 'Select a template and case, then click Generate Document.'}
              </p>
              <p className="text-xs text-muted-foreground mt-1 max-w-sm">
                The AI document engine will automatically merge case facts, party details, and legal clauses.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentGeneratorPage;
